import axios, { AxiosPromise, AxiosResponse } from 'axios';
import qs from 'qs';
import { BaseService } from './BaseService';
import {
    transformVFair,
    VFairOrganization,
    VFairPavilion,
    VFairPavilionGroup,
    VFairResponse,
    VFairSchoolConnection,
    VFairUser,
    VFairWebinar,
    VFairWebinarShallow,
    VFairOrganizationFAQ,
    VFairActivity,
    VFair,
} from './types/vfair';
import UserInfoService from './UserInfoService';

export * from './types/vfair';

export interface GetWebinarsParams {
    shallow?: boolean;
    from?: Date;
    to?: Date;
}

export interface GetVFairParams {
    open: boolean;
    private: boolean;
    network: boolean;
    schoolId: number[];
    organizationId: number[];
    invited: number[];

    endAfter: Date;
    endBefore: Date;
}

class VFairService extends BaseService {
    constructor() {
        super('vfair');
    }

    getVFairsByParams(params: Partial<GetVFairParams>): Promise<VFair[]> {
        return axios({
            method: 'GET',
            url: `${process.env.VUE_APP_API_URL}v1/vfairs`,
            params: {
                ...params,
                endAfter: params.endAfter ? params.endAfter.toISOString().slice(0, 10) : undefined,
                endBefore: params.endBefore
                    ? params.endBefore.toISOString().slice(0, 10)
                    : undefined,
            },
            paramsSerializer: params => {
                return qs.stringify(params, { arrayFormat: 'repeat' });
            },
        }).then((res: AxiosResponse<{ results: VFairResponse[] }>) =>
            res.data.results.map(transformVFair)
        );
    }

    static isAnyOrganizationActiveOnVfair(
        vFairId: string,
        organizationIds: number[]
    ): Promise<boolean> {
        if (organizationIds.length === 0) {
            return Promise.resolve(false);
        }

        const params = {
            vfairId: vFairId,
            organizationId: organizationIds,
        };

        return axios({
            method: 'GET',
            url: `${process.env.VUE_APP_API_URL}v1/vfairs/organizations`,
            params: params,
            paramsSerializer: function (params) {
                return qs.stringify(params, { arrayFormat: 'repeat' });
            },
        })
            .then((result: AxiosResponse<VFairOrganization[]>) => {
                if (result.data && result.data.length > 0) {
                    const found = result.data.find(vFairOrg => vFairOrg.status === 2);
                    return found !== undefined;
                }
                return false;
            })
            .catch(() => {
                return false;
            });
    }

    static isAnySchoolActiveOnVfair(vFairId: string, organizationIds: number[]): Promise<boolean> {
        if (organizationIds.length === 0) {
            return Promise.resolve(false);
        }

        return axios({
            method: 'GET',
            url: `${process.env.VUE_APP_API_URL}v1/vfair/${vFairId}/connection`,
        })
            .then((result: AxiosResponse<VFairSchoolConnection[]>) => {
                if (result.data && result.data.length > 0) {
                    const connectedSchoolIds = result.data
                        .filter(s => s.exposed === true)
                        .map(s => s.schoolId);

                    return organizationIds.some(i => connectedSchoolIds.includes(i));
                }
                return false;
            })
            .catch(() => {
                return false;
            });
    }

    static isSchoolActiveOnVfair(
        vFairId: string
    ): Promise<{ isActive: boolean; activeSchools: number[]; verifiedSchools: number[] }> {
        const schoolIds = UserInfoService.getSchoolAdminIds();

        const response = {
            isActive: false,
            activeSchools: [] as number[],
            verifiedSchools: schoolIds,
        };

        return axios({
            method: 'GET',
            url: `${process.env.VUE_APP_API_URL}v1/vfair/${vFairId}/connection`,
        })
            .then((result: AxiosResponse<VFairSchoolConnection[]>) => {
                if (result.data && result.data.length > 0) {
                    const connectedSchoolIds = result.data
                        .filter(s => s.exposed)
                        .map(s => s.schoolId);

                    response.isActive = schoolIds.some(i => connectedSchoolIds.includes(i));
                    response.activeSchools = connectedSchoolIds;
                }
                return response;
            })
            .catch(() => {
                return response;
            });
    }

    /* User activity */

    static updateUserActivity(vFairId: string): AxiosPromise<VFairActivity[]> {
        return axios({
            method: 'POST',
            url: `${process.env.VUE_APP_API_URL}v1/vfair/${vFairId}/useractivity`,
            data: {
                vfairRole: 'student',
            },
        });
    }

    /* VFairs */

    async getVFair(vFairID: string) {
        return axios({
            method: 'GET',
            url: this.getUrl(vFairID),
        }).then((res: AxiosResponse<VFairResponse>) => transformVFair(res.data));
    }

    /* Pavilions */

    private static getPavilionsUrl(vFairID: string, suffix?: string) {
        return `${process.env.VUE_APP_API_URL}v1/vfair/${vFairID}/pavilions${
            !!suffix ? `/${suffix}` : ''
        }`;
    }

    static getPavilion(vFairID: string, pavilionID: string): AxiosPromise<VFairPavilion> {
        return axios({
            method: 'GET',
            url: this.getPavilionsUrl(vFairID, pavilionID),
        });
    }

    static getPavilions(vFairID: string): AxiosPromise<VFairPavilion[]> {
        return axios({
            method: 'GET',
            url: this.getPavilionsUrl(vFairID),
        });
    }

    static getPavilionGroups(vFairID: string): AxiosPromise<VFairPavilionGroup[]> {
        return axios({
            method: 'GET',
            url: `${process.env.VUE_APP_API_URL}v1/vfair/${vFairID}/paviliongroups`,
        });
    }

    /* Webinars */

    static getWebinars<T extends VFairWebinarShallow = VFairWebinar>(
        vFairID: string,
        params: GetWebinarsParams = {}
    ): AxiosPromise<T[]> {
        const queryParams = new URLSearchParams();
        params.shallow && queryParams.append('shallow', 'true');
        params.from && queryParams.append('from', params.from.toISOString());
        params.to && queryParams.append('to', params.to.toISOString());

        const queryParamsString = queryParams.toString();
        let url = `${process.env.VUE_APP_API_URL}v1/vfair/${vFairID}/events`;
        if (!!queryParamsString) url += `?${queryParamsString}`;

        return axios({
            method: 'GET',
            url,
        });
    }

    static getWebinar(vFairID: string, webinarID: string): AxiosPromise<VFairWebinar> {
        return axios({
            method: 'GET',
            url: `${process.env.VUE_APP_API_URL}v1/vfair/${vFairID}/events/${webinarID}`,
        });
    }

    static setVFairUser(vFairUser: VFairUser, code?: string): Promise<VFairUser> {
        const queryParams = new URLSearchParams();
        if (code) {
            queryParams.append('code', code);
        }

        const queryParamsString = queryParams.toString();

        let url = `${process.env.VUE_APP_API_URL}v1/vfairuser/user/me/${vFairUser.vfairId}`;
        if (!!queryParamsString) {
            url += `?${queryParamsString}`;
        }

        return axios({
            method: 'POST',
            url,
            data: vFairUser,
        })
            .then(({ data }: AxiosResponse) => {
                const user: VFairUser = {
                    userId: data.user_id,
                    vfairId: data.vfair_id,
                    interested: data.interested,
                    invited: data.invited,
                };
                return user;
            })
            .catch(reason => {
                throw reason;
            });
    }

    /* FAQ */

    async getVFairOrganizationFAQs(vFairID: string, organizationId: string | number) {
        return axios({
            method: 'GET',
            url: this.getUrl(`${vFairID}/organizations/${organizationId}/faq`),
        }).then<VFairOrganizationFAQ[]>(res => res.data);
    }
}

export default VFairService;
